/**
 * FRONTIER F9-4 — Adaptive shell facade.
 *
 * One hook the shell chrome consumes to get:
 *   • the current usage scope (tenant + persona),
 *   • whether adaptation is active (has signal AND not opted out),
 *   • the live usage counts,
 *   • a `recordUse` to log a surface open,
 *   • reset / opt-out actions for the transparency control,
 *   • an entitlement-bounded copilot scope (published as a side channel).
 *
 * The hook NEVER receives or resolves entitlement itself. Callers pass in the
 * ALREADY-PERMITTED module ids (resolved by the shell's role-gate + entitlement
 * evaluator) purely so the copilot-scope ordering stays bounded to that set.
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useTenantContext } from '@tyndo/hooks';
import { useNavMode } from '../layout/NavModeContext';
import {
  recordSurfaceUse,
  resetUsage,
  setAdaptationDisabled,
  useAdaptationDisabled,
  useUsageCounts,
  type UsageScope,
} from './usageStore';
import { buildCopilotScope, totalUsage } from './adaptiveNav';
import { publishCopilotScope } from './copilotScope';

export interface AdaptiveShell {
  scope: UsageScope;
  /** Whether ordering is currently usage-adapted (signal present AND not opted out). */
  active: boolean;
  /** Whether the operator opted this scope out of adaptation. */
  optedOut: boolean;
  /** Total recorded interactions in this scope (0 ⇒ honest cold-start). */
  interactions: number;
  /** Live usage counts for the scope. */
  counts: Readonly<Record<string, number>>;
  /** Log one open of a surface (module id). */
  recordUse: (surfaceKey: string) => void;
  /** Clear the learned signal and return to the default ordering. */
  reset: () => void;
  /** Toggle whether adaptation applies for this scope. */
  setOptedOut: (optedOut: boolean) => void;
}

export function useAdaptiveShell(permittedModuleIds: readonly string[]): AdaptiveShell {
  const { tenantId } = useTenantContext();
  const { persona } = useNavMode();

  const scope = useMemo<UsageScope>(() => ({ tenantId, persona }), [tenantId, persona]);

  const counts = useUsageCounts(scope);
  const optedOut = useAdaptationDisabled(scope);
  const interactions = useMemo(() => totalUsage(counts), [counts]);

  // Adaptation only takes effect when there is REAL signal and the operator
  // hasn't opted out. No signal ⇒ default ordering (honest cold-start).
  const active = interactions > 0 && !optedOut;

  const recordUse = useCallback(
    (surfaceKey: string) => recordSurfaceUse(scope, surfaceKey),
    [scope]
  );
  const reset = useCallback(() => resetUsage(scope), [scope]);
  const setOptedOut = useCallback(
    (value: boolean) => setAdaptationDisabled(scope, value),
    [scope]
  );

  // Publish the entitlement-bounded copilot scope as a side channel whenever the
  // permitted set, signal, or active state changes. The copilot rail reads this
  // to emphasize (not widen) suggestions.
  useEffect(() => {
    const config = buildCopilotScope(permittedModuleIds, counts, active);
    publishCopilotScope(config);
  }, [permittedModuleIds, counts, active]);

  return { scope, active, optedOut, interactions, counts, recordUse, reset, setOptedOut };
}
